import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import packages, { apiError, errorMessage, isEndpointMissing } from 'common/packages';

const PAGE_SIZE = 10;

const AccountPickerDialog = (props) => {
  const { open, application, initialQuery, grantedUuids, busy, onGrant, onClose } = props;

  const [query, setQuery] = useState('');
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery || '');
    setTerm(initialQuery || '');
    setPage(0);
    setPending(null);
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      setTerm(query.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [open, query]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    // Built conditionally: the shared paramsSerializer walks every key it is
    // given, so an undefined q would go out as the string "undefined".
    const params = { page: page + 1, page_size: rowsPerPage };
    if (term) params.q = term;
    packages
      .candidateAccounts(application, params)
      .then((data) => {
        if (!active) return;
        setRows((data && data.accounts) || []);
        setTotal((data && data.total_size) || 0);
        setError(null);
      })
      .catch((e) => {
        if (!active) return;
        setRows([]);
        setTotal(0);
        setError(apiError(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, application, term, page, rowsPerPage]);

  const choose = (account) => {
    // An unlicensed grant is legal and inert. Confirm it rather than filtering it
    // out: someone granting a prospect has to see that it did nothing.
    if (account.is_licensed === false) {
      setPending(account);
      return;
    }
    onGrant(account);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Grant access to an account</DialogTitle>

      {pending ? (
        <>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{pending.email || pending.name}</strong> has no licence for{' '}
              <strong>{application}</strong>.
            </Alert>
            <DialogContentText>
              A download needs both a licence and a grant, so this grant will have no
              effect until the account is licensed for this application. The grant is
              still recorded, and it starts working the moment the licence is added —
              but nothing changes for this account today.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPending(null)} disabled={busy}>
              Back
            </Button>
            <Button
              variant="contained"
              color="warning"
              disableElevation
              disabled={busy}
              onClick={() => {
                const account = pending;
                setPending(null);
                onGrant(account);
              }}
            >
              Grant anyway
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                label="Search accounts"
                placeholder="Email or name, any part of it"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />

              {error &&
                (isEndpointMissing(error) ? (
                  <Alert severity="info">
                    Account search is unavailable: this environment's API does not serve
                    the candidate-accounts endpoint yet.
                  </Alert>
                ) : (
                  <Alert severity="error">{errorMessage(error)}</Alert>
                ))}

              <Box sx={{ minHeight: 4 }}>{loading && <LinearProgress />}</Box>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Licence</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((account) => {
                    const already = grantedUuids.indexOf(account.uuid) >= 0;
                    return (
                      <TableRow key={account.uuid} hover>
                        <TableCell>{account.name || '—'}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>
                          {account.email || '—'}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                            {account.is_licensed === false ? (
                              <Chip size="small" color="warning" label="No licence" />
                            ) : (
                              <Chip size="small" variant="outlined" label="Licensed" />
                            )}
                            {already && (
                              <Chip size="small" color="info" label="Already has access" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() => choose(account)}
                          >
                            Grant
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && rows.length === 0 && !error && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          {term
                            ? 'No account matches that search.'
                            : 'No accounts to show.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                onPageChange={(event, value) => setPage(value)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={busy}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

AccountPickerDialog.propTypes = {
  application: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  grantedUuids: PropTypes.array,
  initialQuery: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onGrant: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

AccountPickerDialog.defaultProps = {
  grantedUuids: [],
  initialQuery: '',
};

export default AccountPickerDialog;
